<template>
  <loading-overlay v-if="isLoading"></loading-overlay>
  <div v-else>
    <div class="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center pb-2 mb-3 border-bottom">
      <h1 id="top">Bundle: <small>{{ bundle.name }}</small></h1>
    </div>

    <div class="card">
      <table class="card-table table">
        <tbody>
        <tr>
          <th scope="col">ID</th>
          <td>{{ bundle.id }}</td>
        </tr>
        <tr>
          <th scope="col">Name</th>
          <td>{{ bundle.name }}</td>
        </tr>
        <tr>
          <th scope="col">Status</th>
          <td>{{ bundle.status }}</td>
        </tr>
        <tr>
          <th scope="col">Message</th>
          <td>{{ bundle.system_message }}</td>
        </tr>
        <tr>
          <th scope="col">Street Address</th>
          <td>{{ bundle.street_address_1 }}</td>
        </tr>
        <tr>
          <th scope="col">City</th>
          <td>{{ bundle.city }}</td>
        </tr>
        <tr>
          <th scope="col">State</th>
          <td>{{ bundle.state }}</td>
        </tr>
        <tr>
          <th scope="col">Country</th>
          <td>{{ bundle.country }}</td>
        </tr>
        <tr>
          <th scope="col">Zipcode</th>
          <td>{{ bundle.zipcode }}</td>
        </tr>
        <tr>
          <th scope="col">Bundle Version</th>
          <td>{{ bundle.bundle_version }}</td>
        </tr>
        <tr>
          <th scope="col">MD5 Checksum</th>
          <td>{{ bundle.md5checksum }}</td>
        </tr>
        <tr>
          <th scope="col">Output Type</th>
          <td>{{ bundle.output_type }}</td>
        </tr>
        <tr>
          <th scope="col">Last seen</th>
          <td>{{ bundle.last_seen | dateFormat }}</td>
        </tr>
        <tr>
          <th scope="col">Archived</th>
          <td>
            <div v-if="bundle.archived">
                <span class="badge badge-warning">
                  <font-awesome-icon icon="archive"/>
                  Archived
                </span>
            </div>
            <div v-else>
              <span class="badge badge-success">Not Archived</span>
            </div>
          </td>
        </tr>
        <tr>
          <th scope="col">Created At</th>
          <td>{{ new Date(bundle.created_at).toDateString() }}, {{ new Date(bundle.created_at).toLocaleTimeString() }}</td>
        </tr>
        <tr>
          <th scope="col">Updated At</th>
          <td>{{ new Date(bundle.updated_at).toDateString() }}, {{ new Date(bundle.created_at).toLocaleTimeString() }}</td>
        </tr>
        </tbody>
      </table>
      <div class="card-footer">
        <div class="btn-group">
          <b-button v-if="isAdmin || isKioskEditor || isKioskAdmin" @click="getBundleAnalytics()" class="btn btn-info">Bundle Analytics</b-button>
          <router-link v-if="isAdmin || isKioskEditor || isKioskAdmin" :to="{ name: 'AnalyticIndex', params: { kioskId: kioskId, bundleId: bundle.id }}" tag="button" class="btn btn-primary">Analytics</router-link>
          <router-link :to="{ name: 'BundleIndex', params: { kioskId: kioskId, bundle_id: bundle.id } }" tag="button" class="btn btn-secondary">Back</router-link>
          <router-link v-if="isAdmin" :to="{ name: 'BundleEdit' }" tag="button" class="btn btn-primary">Edit</router-link>
          <a v-if="bundle.executable_link" :to="{ name: 'BundleEdit' }" :href="bundle.executable_link" class="btn btn-info">Download</a>
          <button v-if="isAdmin || isKioskAdmin" v-on:click="republishBundle" type="button" class="btn btn-warning">Republish</button>
          <button v-if="isAdmin" v-on:click="$refs.bundleDeleteModal.show()" type="button" class="btn btn-danger">Delete</button>
        </div>
      </div>
    </div>

    <b-modal ref="bundleDeleteModal" header-bg-variant="danger" header-text-variant="light" class="bundleDeleteModal" title="Confirm Delete" ok-variant="danger" ok-title="Delete" @ok="deleteBundle" v-bind:ok-disabled="mollyGuard !== bundle.name">
      <p>Are you sure you want to delete <span class="font-weight-bold">{{ bundle.name }}</span>?</p>
      <p>To confirm the deletion of this bundle, enter the title of the bundle below:</p>
      <form @submit.stop.prevent autocomplete="off">
        <div class="form-group">
          <input type="text" class="form-control" v-model="mollyGuard" placeholder="Bundle Title"/>
        </div>
      </form>
    </b-modal>
  </div>
</template>

<script>
import { BundleService } from '@/common/services/bundle.service';
import PermissionsService from '@/common/services/user_permissions.service';
import LoadingOverlay from '@/common/components/loading-overlay.vue';
import KioskService from '@/common/services/kiosk.service';

export default {
  name: 'BundleShow',
  components: {
    LoadingOverlay,
  },
  data() {
    return {
      bundle: null,
      kioskId: null,
      bundleId: null,
      isLoading: true,
      totalRows: 0,
      mollyGuard: null,
      isAdmin: false,
      isKioskAdmin: false,
      isKioskEditor: false,
      isKioskViewer: false,
    };
  },
  created() {
    this.kioskId = this.$route.params.kioskId;
    this.bundleId = this.$route.params.id;
    this.getBundle();
  },
  methods: {
    getBundle() {
      BundleService.get(this.kioskId, this.bundleId)
        .then((response) => {
          this.bundle = response.data;
          this.isLoading = false;
          return this.checkPermissions();
        })
        .then((permissions) => {
          [this.isAdmin, this.isKioskAdmin, this.isKioskEditor, this.isKioskViewer] = permissions;
        })
        .catch(err => console.error(err));
    },
    deleteBundle() {
      BundleService.destroy(this.kioskId, this.bundleId)
        .then((response) => {
          this.$router.push({ name: 'BundleIndex' });
          this.$notify({
            title: `Deleted ${response.data.name}`,
            text: 'Successfully deleted bundle.',
            type: 'success',
            width: 350,
          });
        });
    },
    checkPermissions() {
      const adminPromise = PermissionsService.isAdmin();
      const kioskAdminPromise = PermissionsService.isCurrKioskAdmin(this.kioskId);
      const kioskEditorPromise = PermissionsService.isCurrKioskEditor(this.kioskId);
      const kioskViewerPromise = PermissionsService.isCurrKioskViewer(this.kioskId);

      return Promise.all([adminPromise, kioskAdminPromise, kioskEditorPromise, kioskViewerPromise]);
    },
    republishBundle() {
      return BundleService.republish(this.kioskId, this.bundleId).then(() => {
        this.getBundle();
      });
    },
    getBundleAnalytics() {
      KioskService.bundleAnalytics(this.kioskId, this.bundleId)
        .then((response) => {
          const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/vnd.ms-excel' }));
          const link = document.createElement('a');
          link.href = url;
          const date = new Date();
          link.setAttribute('download', `Bundle-${this.bundleId}-Analytics-${date.toISOString().substring(0, 10)}.xlsx`);
          link.setAttribute('target', '_blank');
          link.click();
        })
        .catch(err => console.error('index getReports -> kioskAnalytics', err));
    },
  },
  beforeRouteUpdate(to, from, next) {
    BundleService.get(to.params.id)
      .then((response) => {
        this.bundle = response.data;
        this.isLoading = false;
        return this.checkPermissions();
      })
      .then((permissions) => {
        [this.isAdmin, this.isKioskAdmin, this.isKioskEditor, this.isKioskViewer] = permissions;
      })
      .then(() => {
        next();
      })
      .catch(err => console.error(err));
  },
};
</script>

<style lang="scss" scoped>
</style>
